// @client/components/profile-manager/ProfilePagination.tsx
import React from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Button } from "@client/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@client/components/ui/select";

export interface ProfilePaginationProps {
  pagination: {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    total: number;
    startIndex: number;
    endIndex: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  entityName?: string;
}

export function ProfilePagination({
  pagination,
  onPageChange,
  onLimitChange,
  entityName = 'profiles'
}: ProfilePaginationProps) {
  
  if (!pagination || pagination.totalPages <= 1) {
    return null;
  }

  const { 
    currentPage, 
    totalPages, 
    hasNextPage, 
    hasPreviousPage, 
    startIndex, 
    endIndex, 
    total,
    pageSize 
  } = pagination;

  const generatePageNumbers = (): number[] => {
    const maxVisiblePages = 7;
    const pages: number[] = [];

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= Math.min(5, totalPages); i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 3) {
        for (let i = Math.max(totalPages - 4, 1); i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = generatePageNumbers();
  const needsEllipsisBefore = pageNumbers.length > 1 && pageNumbers[1] > 2;
  const needsEllipsisAfter = pageNumbers.length > 1 && pageNumbers[pageNumbers.length - 2] < totalPages - 1;

  const pageSizeOptions = [
    { value: 20, label: '20 per page' },
    { value: 50, label: '50 per page' },
    { value: 100, label: '100 per page' },
    { value: 200, label: '200 per page' }
  ];

  const handleFirstPage = () => onPageChange(1);
  const handlePreviousPage = () => hasPreviousPage && onPageChange(currentPage - 1);
  const handleNextPage = () => hasNextPage && onPageChange(currentPage + 1);
  const handleLastPage = () => onPageChange(totalPages);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
      <div className="text-sm text-muted-foreground">
        Showing <span className="font-medium">{startIndex}</span> to{' '}
        <span className="font-medium">{endIndex}</span> of{' '}
        <span className="font-medium">{total}</span> {entityName}
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleFirstPage}
          disabled={!hasPreviousPage}
          className="h-8 w-8 p-0"
          title="First page"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={handlePreviousPage}
          disabled={!hasPreviousPage}
          className="h-8 w-8 p-0"
          title="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-1">
          {pageNumbers.map((pageNum, index) => {
            const isCurrentPage = pageNum === currentPage;
            const isFirst = index === 0;
            const isLast = index === pageNumbers.length - 1;
            
            return (
              <React.Fragment key={pageNum}>
                {needsEllipsisBefore && isFirst && pageNum === 1 && pageNumbers[1] > 2 && (
                  <>
                    <Button
                      variant={isCurrentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => onPageChange(pageNum)}
                      className="h-8 w-8 p-0"
                    >
                      {pageNum}
                    </Button>
                    <span className="text-muted-foreground px-1">…</span>
                  </>
                )}
                
                {!(needsEllipsisBefore && isFirst && pageNum === 1) && (
                  <Button
                    variant={isCurrentPage ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(pageNum)}
                    className="h-8 w-8 p-0"
                  >
                    {pageNum}
                  </Button>
                )}
                
                {needsEllipsisAfter && isLast && pageNum === totalPages && pageNumbers[pageNumbers.length - 2] < totalPages - 1 && (
                  <>
                    <span className="text-muted-foreground px-1">…</span>
                    <Button
                      variant={isCurrentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => onPageChange(pageNum)}
                      className="h-8 w-8 p-0"
                    >
                      {pageNum}
                    </Button>
                  </>
                )}
              </React.Fragment>
            );
          })}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleNextPage}
          disabled={!hasNextPage}
          className="h-8 w-8 p-0"
          title="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={handleLastPage}
          disabled={!hasNextPage}
          className="h-8 w-8 p-0"
          title="Last page"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Select
          value={pageSize.toString()}
          onValueChange={(value) => onLimitChange(parseInt(value))}
        >
          <SelectTrigger className="w-[140px] h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map(option => (
              <SelectItem key={option.value} value={option.value.toString()}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}